/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Heading,
  Icon,
  TextField,
  useToast,
} from "@elaniintc/goes-design-system";
import type { FormSection, FormPage, FormDesignProps } from "./types";

import { ContentForm } from "./components/ContentForm";

import InputModalBase from "./components/InputModalBase";
import SortStepsFormDesignModal from "./components/SortStepsFormDesignModal";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import { ConfirmationModalBase } from "@/components/common/modals";
import FullModal from "../../full-modal";
import { useUIStore } from "@/workflow_api/store/uiStore";
import {
  deformatForm,
  formattedForm,
  applyDynamicDates,
} from "./FormDataFormatter";
import {
  createDataByIdFetcher,
  createDataCenterFetcher,
  createInteroperabilityFetcher,
  useCreateForm,
  useGetForm,
  useUpdateForm,
  useUpdateNode,
} from "@/workflow_api/services/nodes.api";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import type { NodeData, RefName } from "@/workflow_api/types/workflow";

import Variables from "@/workflow_api/components/variables";
import { FormOutputVariabels } from "@/workflow_api/components/variables/form/output";
import InfoMessage from "./components/InfoMessage";
import ScrollModal from "@/components/common/scroll-modal";

import {
  ExternalDataProvider,
  validateRequiredFields,
  type Step,
} from "@sdt/render-form-engine";

import { MultiStepForm } from "@sdt/render-form-engine";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/theme";
import { GroupSelector } from "../assign-group/components/group-selector";
import { GroupsProvider, useGroups } from "../assign-group/context";
import type { GroupType } from "@/types/entities/approval-groups";
import { Renderify } from "renderify-js";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";

const GroupAssignmentSection: React.FC<{
  onGroupSelect: (groupId: string | undefined) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assignedGroup: any | undefined;
}> = ({ onGroupSelect, assignedGroup }) => {
  const {
    groups,
    selectedGroup,
    selectGroup,
    loadMore,
    hasMore,
    isLoadingMore,
  } = useGroups();

  const [hasError, setHasError] = useState(false);

  const handleGroupChange = (group: GroupType) => {
    selectGroup(group);
    setHasError(false);
    onGroupSelect(group.id);
  };

  useEffect(() => {
    if (assignedGroup && groups.length > 0) {
      const foundGroup = groups.find(
        (group) => group.id === assignedGroup.value,
      );

      if (foundGroup) {
        selectGroup(foundGroup);
        onGroupSelect(foundGroup.id);
      }
    }
  }, [assignedGroup, groups, selectGroup]);

  return (
    <div className="border-b border-neutral-quiet p-4 flex flex-col gap-2">
      <label className="block text-sm font-medium text-body-loud">
        Asignar grupo <span className="text-danger">*</span>
      </label>
      <GroupSelector
        groups={groups}
        selectedGroup={selectedGroup}
        onGroupChange={handleGroupChange}
        placeholder="Seleccionar grupo"
        onLoadMore={loadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        selectedGroupsList={selectedGroup ? [selectedGroup] : []}
      />
      {hasError && (
        <p className="text-sm text-danger">Debes seleccionar un grupo</p>
      )}
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---

export const FormDesign: React.FC<FormDesignProps> = ({
  initialPages = [],
  onPreview,
}) => {
  const { selectedNodeId } = useUIStore();
  const { getNodeById, flowId } = useWorkflowStore();

  const node = getNodeById(selectedNodeId!);

  const assignedGroup = (node?.data as unknown as NodeData)?.inputs?.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (input: any) => input.type === "GROUP",
  );

  type FormInputs = {
    id: string;
    name: string;
    type: string;
    value: string;
  };

  // obtener formulario si existiese uno
  const { data: formData, isFetching } = useGetForm(
    (node?.data.inputs as FormInputs[])?.find((input) => input.type === "FORM")
      ?.value ?? "",
  );

  // State management
  const [pages, setPages] = useState<FormPage[]>(() => {
    if (formData) {
      try {
        return deformatForm(formData.data);
      } catch {
        // Error deformateando datos del formulario
      }
    }

    if (initialPages.length > 0) {
      return initialPages;
    }
    // Create default page with a default section
    const defaultSection: FormSection = {
      id: "section_1",
      name: "",
      technicalKey: "seccion_1",
      description: "",
      fields: [],
      isCollapsed: false,
    };

    return [
      {
        id: "page_1",
        name: "Paso 1",
        sections: [defaultSection],
      },
    ];
  });

  // Initialization
  useEffect(() => {
    if (formData?.data) {
      try {
        const loaded = deformatForm(formData.data);
        setPages(loaded);
        if (loaded.length) setSelectedPageId(loaded[0].id);
      } catch {
        // Error deformateando datos del formulario
      }
    }
  }, [formData]);

  const [selectedPageId, setSelectedPageId] = useState<string>("page_1");
  const [showDeleteStepModal, setShowDeleteStepModal] = useState(false);
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);
  const [isDraggingSection, setIsDraggingSection] = useState(false);
  const [showRenameStepModal, setShowRenameStepModal] = useState(false);
  const [showReorderStepModal, setShowReorderStepModal] = useState(false);
  const [stepToRename, setStepToRename] = useState<string | null>(null);
  const [renameStepValue, setRenameStepValue] = useState("");
  const [renameStepError, setRenameStepError] = useState("");
  const [stepToDelete, setStepToDelete] = useState<string | null>(null);
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [addStepValue, setAddStepValue] = useState("");
  const [addStepError, setAddStepError] = useState("");

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const nodeData = node?.data as unknown as NodeData;
  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  // Actualizar manualConfirmation cuando nodeData cambie
  useEffect(() => {
    if (nodeData?.manual_continue !== undefined) {
      setManualConfirmation(nodeData.manual_continue);
    }
  }, [nodeData?.manual_continue]);

  useEffect(() => {
    if (!pages.length) return;
    const exists = pages.some((p) => p.id === selectedPageId);
    if (!exists) {
      setSelectedPageId(pages[0].id);
    }
  }, [pages, selectedPageId]);

  const queryClient = useQueryClient();

  const fetchExternalData = useMemo(
    () => createDataCenterFetcher(queryClient),
    [queryClient],
  );

  const fetchDataById = useMemo(() => {
    return createDataByIdFetcher(queryClient);
  }, [queryClient]);

  type FormNodeErrors = { errors: Record<string, string[]> };
  const [formNodeErrors, setFormNodeErrors] = useState<FormNodeErrors>({
    errors: {},
  });

  const flattenErrors = (errors: Record<string, string[]>) =>
    Object.entries(errors).flatMap(([path, messages]) =>
      (messages ?? []).map((message) => ({ path, message })),
    );

  const errorsList = useMemo(
    () => flattenErrors(formNodeErrors.errors || {}),
    [formNodeErrors.errors],
  );

  const createForm = useCreateForm();
  const updateForm = useUpdateForm();
  const { mutateAsync: updateNode } = useUpdateNode(flowId);

  // ----------- Externo o Interno ------------------

  const [targetUserType, setTargetUserType] = useState(true);

  useEffect(() => {
    if (formData?.data?.target_user_type) {
      setTargetUserType(formData.data.target_user_type === "applicant");
    }
  }, [formData?.data?.target_user_type]);

  // crear formulario + actualizar nodo
  const handleSave = async (): Promise<void> => {
    if (!selectedNodeId) {
      setFormNodeErrors({ errors: { general: ["No hay nodo seleccionado"] } });
      throw new Error("No hay nodo seleccionado");
    }
    if (!node) {
      setFormNodeErrors({ errors: { general: ["No hay nodo seleccionado"] } });
      throw new Error("No hay nodo seleccionado");
    }

    if (!targetUserType && !selectedGroupId) {
      setFormNodeErrors({ errors: { general: ["Debes asignar un grupo"] } });
      throw new Error("Debes asignar un grupo");
    }

    const currentData = node.data as unknown as NodeData;

    const existingFormId =
      ((currentData.inputs as FormInputs[] | undefined)?.find(
        (input) => input.type === "FORM",
      )?.value as string | undefined) ?? undefined;

    const formPayload = formattedForm(
      title,
      pages,
      existingFormId,
      targetUserType ? "applicant" : "analyst",
    );

    // Helpers
    const safelySetErrors = (err: unknown) => {
      const apiErrors =
        (isAxiosErrorLike(err) ? err.response?.data?.errors : undefined) ?? {};
      setFormNodeErrors({ errors: apiErrors });
    };

    const persistNodeUpdate = async (formId?: string, formName?: string) => {
      const baseInputs = (currentData.inputs as FormInputs[] | undefined) ?? [];

      const otherInputs = baseInputs.filter(
        (input) => input.name !== "form" && input.name !== "assigned_group_id",
      );

      const newInputs: (
        | FormInputs
        | { name: string; value: string; type: string }
      )[] = [...otherInputs];

      if (formId && formName) {
        newInputs.push({
          name: "form",
          value: formId,
          type: "FORM",
        });
      } else {
        const existingFormInput = baseInputs.find(
          (input) => input.name === "form",
        );
        if (existingFormInput) {
          newInputs.push(existingFormInput);
        }
      }

      if (!targetUserType) {
        newInputs.push({
          name: "assigned_group_id",
          value: selectedGroupId || "",
          type: "GROUP",
        });
      }

      await updateNode({
        node_id: node.id,
        node: {
          display_name: title || currentData.displayName,
          flow_id: flowId,
          ref_name: currentData.type as RefName,
          order: currentData.order,
          position_x: node.position.x,
          position_y: node.position.y,
          type: currentData.node_type,
          manual_continue: manualConfirmation,
          inputs: newInputs,
        },
      });
    };

    try {
      if (existingFormId) {
        await updateForm.mutateAsync({ form: formPayload });
        await persistNodeUpdate();
        setFormNodeErrors({ errors: {} });
      } else {
        const data = await createForm.mutateAsync({ form: formPayload });
        await persistNodeUpdate(data?.data?.id, data?.data?.name);
        setFormNodeErrors({ errors: {} });
      }
    } catch (err) {
      safelySetErrors(err);
      throw err;
    }
  };

  const [showPreview, setshowPreview] = useState(false);

  // ------------ Preview -------------------
  const handlePreview = () => {
    onPreview?.(pages);
    setshowPreview(true);
  };

  const handleOpenRenameStep = (stepId: string) => {
    setStepToRename(stepId);
    const step = pages.find((p) => p.id === stepId);
    setRenameStepValue(step ? step.name : "");
    setShowRenameStepModal(true);
  };
  const handleOpenReorderStep = () => {
    setShowReorderStepModal(true);
  };
  const handleOpenDeleteStep = (stepId: string) => {
    setStepToDelete(stepId);
    setShowDeleteStepModal(true);
  };
  const handleRenameStepSave = () => {
    if (!stepToRename) return;
    const trimmed = renameStepValue.trim();
    const currentStep = pages.find((p) => p.id === stepToRename);
    if (!currentStep) return;
    if (currentStep.name === trimmed) {
      setShowRenameStepModal(false);
      setStepToRename(null);
      setRenameStepValue("");
      setRenameStepError("");
      return;
    }
    if (
      pages.some(
        (p) =>
          p.name.toLowerCase() === trimmed.toLowerCase() &&
          p.id !== stepToRename,
      )
    ) {
      setRenameStepError("El nombre ya existe.");
      return;
    }
    setPages(
      pages.map((p) => (p.id === stepToRename ? { ...p, name: trimmed } : p)),
    );
    setShowRenameStepModal(false);
    setStepToRename(null);
    setRenameStepValue("");
    setRenameStepError("");
  };
  const handleDeleteStepConfirm = () => {
    if (stepToDelete) {
      const newPages = pages.filter((p) => p.id !== stepToDelete);
      if (selectedPageId === stepToDelete && newPages.length > 0) {
        setSelectedPageId(newPages[0].id);
      }
      setPages(newPages);
    }
    setShowDeleteStepModal(false);
    setStepToDelete(null);
  };

  const handleAddStepSave = () => {
    const trimmed = addStepValue.trim();
    if (!trimmed) {
      setAddStepError("El nombre no puede estar vacío.");
      return;
    }
    if (pages.some((p) => p.name.toLowerCase() === trimmed.toLowerCase())) {
      setAddStepError("El nombre ya existe.");
      return;
    }
    const newId = `page_${Date.now()}`;
    const defaultSection = {
      id: `section_${Date.now()}`,
      name: "",
      technicalKey: "seccion_1",
      description: "",
      fields: [],
      isCollapsed: false,
    };
    const newPage = {
      id: newId,
      name: trimmed,
      sections: [defaultSection],
    };
    setPages([...pages, newPage]);
    setSelectedPageId(newId);
    setShowAddStepModal(false);
    setAddStepValue("");
    setAddStepError("");
  };

  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const { nodeDetailsOpen, setNodeDetailsOpen, setSelectedNodeId } =
    useUIStore();

  const [title, setTitle] = useState(
    (node?.data as unknown as NodeData)?.displayName || "",
  );

  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);

  const { showToast } = useToast();

  const fetchInteroperability = useMemo(
    () => createInteroperabilityFetcher(queryClient /* toast*/),
    [queryClient /* toast*/],
  );

  return (
    <>
      <div className="">
        <ScrollModal
          modalContentClassName="min-w-[98%] min-h-[98%] py-12 px-4"
          modalChildrenClassName=""
          isOpen={showPreview}
          onClose={() => {
            setshowPreview(false);
          }}
        >
          <div className="max-h-full h-full flex flex-col gap-6 flex-1 no-portal-padding">
            <div className="flex items-center">
              <Heading
                variant="h2"
                className="px-4 tablet:px-4 laptop:px-6 desktop:px-8"
              >
                Vista previa
              </Heading>
              <Button
                onClick={() => {
                  setshowPreview(false);
                }}
                size="lg"
                variant="quiet"
                className="ml-auto mr-8"
              >
                <Icon name="times" type="far" />
              </Button>
            </div>

            <ExternalDataProvider
              fetchData={fetchExternalData}
              fetchInteroperability={fetchInteroperability}
              fetchDataById={fetchDataById}
            >
              <MultiStepForm
                assignmentId={title + Date.now()}
                status="IN_PROGRESS"
                formClassName="flex-1"
                handleSaveForm={(formData: { [x: string]: unknown }) => {
                  const previewSteps = formattedForm(
                    title,
                    pages,
                    targetUserType ? "applicant" : "analyst",
                  ).steps as Step[];

                  // Extraer los campos del objeto formData
                  const fields =
                    (formData as { fields?: { [x: string]: unknown } })
                      .fields || {};

                  // console.log("Campos extraídos para validación:", fields);
                  // console.log("Pasos a validar:", previewSteps);

                  const { isValid } = validateRequiredFields(
                    fields,
                    previewSteps,
                  );

                  if (!isValid) {
                    showToast({
                      title: "Campos obligatorios faltantes",
                      description:
                        "Por favor, complete todos los campos requeridos",
                      options: {
                        type: "error",
                        duration: 5000,
                      },
                    });
                    return;
                  }

                  showToast({
                    title: "Formulario válido",
                    description:
                      "Todos los campos obligatorios han sido completados correctamente.",
                    options: {
                      type: "success",
                      duration: 3000,
                    },
                  });
                  setshowPreview(false);
                }}
                steps={applyDynamicDates(
                  formattedForm(
                    title,
                    pages,
                    targetUserType ? "applicant" : "analyst",
                  ).steps as Step[],
                )}
                onToast={(t) => {
                  showToast(t);
                }}
              />
            </ExternalDataProvider>
          </div>
        </ScrollModal>
      </div>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId(undefined);
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <div className="">
        <FullModal
          isSaveValid={isNameValid(title)}
          isOpen={nodeDetailsOpen && !showPreview}
          onClose={() => {
            setModalState("close-warning");
          }}
          onSave={async () => {
            setShowNodeTitleValidation(true);
            if (!isNameValid(title)) return;
            try {
              await handleSave();
              setNodeDetailsOpen(false);
              setSelectedNodeId(undefined);
            } catch (e) {
              console.error(e);
            }
          }}
        >
          <ThreeColumnLayout
            leftHeader={{
              icon: "arrow-right-to-bracket",
              title: "Inputs",
              color: "neutral",
            }}
            centerHeader={{
              icon: "bars",
              title: title,
              color: "action",
              editable: true,
              onTitleChange: (newTitle: string) => {
                setTitle(newTitle);
                if (newTitle.trim() === "") {
                  setShowNodeTitleValidation(true);
                }
              },
              onTitleBlur: () => setShowNodeTitleValidation(true),
              validationError: getNameValidationError(title),
              showValidation: showNodeTitleValidation,
              manualConfirmation,
              showManualConfirmation: false,
              onManualConfirmationChange: setManualConfirmation,
              rightContent: (
                <>
                  <Button
                    size="xs"
                    variant="ghost"
                    className={cn(
                      "font-normal",
                      targetUserType
                        ? "bg-surface-action-accent-quiet border-action text-action"
                        : "bg-surface-info-accent-quiet border-info text-info",
                    )}
                    onClick={() => {
                      setTargetUserType(!targetUserType);
                    }}
                  >
                    {targetUserType ? "Externo" : "Interno"}
                    <Icon
                      name={targetUserType ? "user-circle" : "users-rectangle"}
                      type="fas"
                    />
                  </Button>

                  <Button variant="tertiary" size="sm" onClick={handlePreview}>
                    Preview
                  </Button>
                </>
              ),
            }}
            rightHeader={{
              icon: "arrow-right-from-bracket",
              title: "Outputs",
              color: "neutral",
            }}
            leftContent={
              <>
                <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                  <Variables sourceNodeId={selectedNodeId!} />
                </div>
              </>
            }
            centerContent={
              <>
                {isFetching ? (
                  <div className="w-[520px]">
                    <p className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                      <Icon
                        type="far"
                        name="spinner-third"
                        className="animate-spin text-xl text-body"
                      />
                    </p>
                  </div>
                ) : (
                  <>
                    {errorsList.length > 0 && (
                      <div className="p-4 text-danger text-xs bg-surface-danger-accent">
                        <ul className="space-y-2">
                          {errorsList.map((err, i) => (
                            <InfoMessage
                              key={`${err.path}-${i}`}
                              className="text-danger!"
                            >
                              {err.message}
                            </InfoMessage>
                          ))}
                        </ul>
                      </div>
                    )}

                    <GroupsProvider inputs={nodeData?.inputs || []}>
                      <Renderify condition={!targetUserType}>
                        <GroupAssignmentSection
                          onGroupSelect={(id) => {
                            setSelectedGroupId((id as string) || null);
                          }}
                          assignedGroup={assignedGroup}
                        />
                      </Renderify>
                    </GroupsProvider>

                    <ContentForm
                      pages={pages}
                      setPages={setPages}
                      selectedPageId={selectedPageId}
                      setSelectedPageId={setSelectedPageId}
                      isDraggingSection={isDraggingSection}
                      setIsDraggingSection={setIsDraggingSection}
                      showDeleteStepModal={showDeleteStepModal}
                      setShowDeleteStepModal={setShowDeleteStepModal}
                      showDeleteSectionModal={showDeleteSectionModal}
                      setShowDeleteSectionModal={setShowDeleteSectionModal}
                      setSectionToDelete={setSectionToDelete}
                      onRenameStep={handleOpenRenameStep}
                      onReorderStep={handleOpenReorderStep}
                      onDeleteStep={handleOpenDeleteStep}
                      onAddStep={() => setShowAddStepModal(true)}
                    />
                  </>
                )}
              </>
            }
            rightContent={
              <>
                <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                  {isFetching ? (
                    <div className="w-full pl-4 pt-8">
                      <p className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                        <Icon
                          type="far"
                          name="spinner-third"
                          className="animate-spin text-xl text-body"
                        />
                      </p>
                    </div>
                  ) : (
                    <FormOutputVariabels pages={pages} display_name={title} />
                  )}
                </div>
              </>
            }
          />
        </FullModal>
      </div>

      {/* Delete Step Modal */}
      {showDeleteStepModal && (
        <ConfirmationModalBase
          isOpen={showDeleteStepModal}
          onClose={() => setShowDeleteStepModal(false)}
          onAccept={handleDeleteStepConfirm}
          title="Eliminar paso"
          description="¿Estás seguro de que deseas eliminar este paso? Se eliminarán todas sus secciones y campos."
          acceptText="Eliminar paso"
          acceptVariant="danger-secondary"
          isLoading={false}
        />
      )}

      {/* Delete Section Modal */}
      {showDeleteSectionModal && (
        <ConfirmationModalBase
          isOpen={showDeleteSectionModal}
          onClose={() => setShowDeleteSectionModal(false)}
          onAccept={() => {
            setPages((pages) =>
              pages.map((page) =>
                page.id === selectedPageId
                  ? {
                      ...page,
                      sections: page.sections.filter(
                        (s) => s.id !== sectionToDelete,
                      ),
                    }
                  : page,
              ),
            );
            setShowDeleteSectionModal(false);
            setSectionToDelete(null);
          }}
          title="Eliminar sección"
          description="¿Estás seguro de que deseas eliminar esta sección? Se eliminarán todos sus campos."
          acceptText="Eliminar sección"
          acceptVariant="danger-secondary"
          isLoading={false}
        />
      )}

      {/* Rename Step Modal */}
      {showRenameStepModal && (
        <InputModalBase
          isOpen={showRenameStepModal}
          onClose={() => {
            setShowRenameStepModal(false);
            setStepToRename(null);
            setRenameStepValue("");
            setRenameStepError("");
          }}
          onAccept={handleRenameStepSave}
          title="Renombrar paso"
          description="Elige un nuevo nombre para este paso."
          acceptText="Guardar"
          disabledAccept={renameStepValue.trim() === ""}
          children={
            <TextField
              label="Nombre del paso"
              placeholder="Nombre del paso"
              required
              value={renameStepValue}
              onChange={(e) => {
                setRenameStepValue(e.target.value);
                setRenameStepError("");
              }}
              error={renameStepError}
            />
          }
        />
      )}

      {/* Reorder Steps Modal */}
      {showReorderStepModal && (
        <SortStepsFormDesignModal
          open={showReorderStepModal}
          onClose={() => setShowReorderStepModal(false)}
          onSuccess={() => setShowReorderStepModal(false)}
          pages={pages}
          onReorder={setPages}
        />
      )}

      {/* Add Step Modal */}
      {showAddStepModal && (
        <InputModalBase
          isOpen={showAddStepModal}
          onClose={() => {
            setShowAddStepModal(false);
            setAddStepValue("");
            setAddStepError("");
          }}
          onAccept={handleAddStepSave}
          title="Nuevo paso"
          description="Elige un nombre para este nuevo paso."
          acceptText="Crear paso"
          disabledAccept={addStepValue.trim() === ""}
          children={
            <>
              <TextField
                label="Nombre del paso"
                placeholder="Nombre del paso"
                required
                value={addStepValue}
                onChange={(e) => setAddStepValue(e.target.value)}
                error={addStepError}
              />
            </>
          }
        />
      )}
    </>
  );
};

export default FormDesign;

function isAxiosErrorLike(
  e: unknown,
): e is { response?: { data?: { errors?: Record<string, string[]> } } } {
  return typeof e === "object" && e !== null && "response" in e;
}
